import { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  Search, DollarSign, CheckCircle, ArrowRight, Clock,
  Shield, Target, Zap, Users, TrendingUp,
  AlertTriangle, ChevronDown, Calendar
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DealCard from "../components/DealCard";
import { PRICING_DISCLAIMER } from "../lib/formatSpecial";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const DealConciergePage = () => {
  const { toast } = useToast();
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [matchedDeals, setMatchedDeals] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    budget: "", moveDate: "",
    areas: "", credit: "",
    challenge: "", timeline: ""
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  const budgetRanges = [
    { value: "800-1200", label: "$800 – $1,200/mo", min: 800, max: 1200 },
    { value: "1200-1600", label: "$1,200 – $1,600/mo", min: 1200, max: 1600 },
    { value: "1600-2200", label: "$1,600 – $2,200/mo", min: 1600, max: 2200 },
    { value: "2200-3000", label: "$2,200 – $3,000/mo", min: 2200, max: 3000 },
    { value: "3000+", label: "$3,000+/mo", min: 3000, max: 5000 },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const budgetInfo = budgetRanges.find((b) => b.value === form.budget);
    const extras = [
      form.credit && `Credit Range: ${form.credit}`,
      form.timeline && `Move Timeline: ${form.timeline}`,
      form.challenge && `Biggest Challenge: ${form.challenge}`,
    ].filter(Boolean).join("\n");

    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        lead_type: "renter",
        source: "concierge",
        budget_min: budgetInfo?.min || null,
        budget_max: budgetInfo?.max || null,
        move_in_date: form.moveDate || null,
        preferred_areas: form.areas ? form.areas.split(",").map((a) => a.trim()).filter(Boolean) : [],
        message: extras || null,
      });

      // Fetch matched deals for success screen
      try {
        const params = new URLSearchParams();
        if (budgetInfo?.min) params.set("budget_min", budgetInfo.min);
        if (budgetInfo?.max) params.set("budget_max", budgetInfo.max);
        if (form.areas) params.set("area", form.areas.split(",")[0].trim());
        params.set("limit", "3");
        const dealRes = await axios.get(`${API}/featured-deals?${params}`);
        setMatchedDeals(dealRes.data);
      } catch { /* Non-blocking */ }

      setSubmitted(true);
    } catch {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50">
        <div className="container-main py-12">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-lg mx-auto shadow-xl mb-12" data-testid="concierge-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-3 font-['Playfair_Display']">
              You're All Set
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              I'm reviewing your request now.
            </p>
            <div className="bg-[#0A2342]/[0.03] border border-[#0A2342]/10 rounded-xl p-5 mb-5">
              <p className="text-[#0A2342] font-medium mb-1">Next step:</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Book a quick call so I can lock in the best deals for you.
              </p>
            </div>
            <a
              href="/book"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#DE6E4B] hover:bg-[#c5593d] text-white font-semibold px-8 py-3.5 rounded-lg transition-all shadow-lg shadow-[#DE6E4B]/20"
              data-testid="success-calendly-btn"
            >
              <Calendar className="w-5 h-5" /> Book a Quick Call
            </a>
          </motion.div>

          {/* Matched Deals */}
          {matchedDeals.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="text-center mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-[#0A2342] font-['Playfair_Display'] mb-2" data-testid="first-matches-title">
                  Here Are Your First Matches
                </h3>
                <p className="text-slate-500 text-sm">Based on your preferences — full details coming to your email shortly.</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {matchedDeals.map((deal, i) => (
                  <DealCard key={deal.id || i} deal={deal} index={i} cta="external" />
                ))}
              </div>
              <p className="mt-4 text-center text-xs text-slate-500" data-testid="pricing-disclaimer">{PRICING_DISCLAIMER}</p>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen">
      {/* ───────── SECTION 1: HERO ───────── */}
      <section className="relative bg-[#0A2342] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="container-main relative z-10 py-20 md:py-28 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#DE6E4B]/20 text-[#DE6E4B] rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" /> Deal Concierge Service
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Playfair_Display'] leading-tight max-w-4xl mx-auto" data-testid="hero-headline">
              Stop Guessing on Apartments.<br className="hidden md:block" /> Let Us Find You the Best Deal in Houston.
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              We help renters secure undervalued apartments, hidden concessions, and smarter lease decisions&mdash;without wasting hours searching.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToForm}
                className="bg-[#DE6E4B] hover:bg-[#c5593d] text-white font-semibold px-8 py-4 rounded-lg transition-all shadow-lg shadow-[#DE6E4B]/25 flex items-center justify-center gap-2 text-base"
                data-testid="hero-cta-primary">
                Apply for Concierge Service <ArrowRight className="w-5 h-5" />
              </button>
              <a href="/specials"
                className="border border-white/30 text-white hover:bg-white/10 font-medium px-8 py-4 rounded-lg transition-all flex items-center justify-center gap-2 text-base"
                data-testid="hero-cta-secondary">
                <Zap className="w-5 h-5" /> Get Hidden Deals Instead
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 2: PROBLEM ───────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-main max-w-3xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-8 font-['Playfair_Display'] text-center" data-testid="problem-title">
              Most Renters Are Overpaying Without Realizing It
            </h2>
            <div className="space-y-4">
              {[
                "Listings online are often outdated or already picked over",
                "Most renters don't know how to spot real deals",
                "Applying blindly leads to wasted time and money"
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 bg-red-50/60 border border-red-100 rounded-xl p-4">
                  <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-sm md:text-base">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 3: SOLUTION ───────── */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container-main max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#8A9A5B]/15 text-[#8A9A5B] rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" /> The Solution
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-5 font-['Playfair_Display']" data-testid="solution-title">
              Introducing the UNQ Deal Concierge
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              This is not just apartment locating. This is a guided strategy to help you find, evaluate, and secure the best possible deal based on your situation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 4: WHAT WE DO ───────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-main">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-10 font-['Playfair_Display'] text-center" data-testid="services-title">
            What We Do For You
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Search, title: "Deal Sourcing", desc: "Find off-market units, pricing gaps, and hidden concessions" },
              { icon: Target, title: "Smart Matching", desc: "Only show options that fit your budget, lifestyle, and approval odds" },
              { icon: Shield, title: "Application Strategy", desc: "Guide when and where to apply to maximize approval chances" },
              { icon: DollarSign, title: "Deal Optimization", desc: "Help you avoid fees and secure the best lease terms" },
            ].map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-slate-50 rounded-xl p-6 hover:shadow-lg hover:bg-white transition-all border border-transparent hover:border-slate-100 group"
                data-testid={`service-block-${i}`}>
                <div className="w-12 h-12 bg-[#0A2342] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#DE6E4B] transition-colors">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#0A2342] mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── SECTION 5: RESULTS ───────── */}
      <section className="py-16 md:py-20 bg-[#0A2342]">
        <div className="container-main max-w-3xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 font-['Playfair_Display'] text-center" data-testid="results-title">
              What This Means For You
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: DollarSign, value: "$1,000+", label: "Saved over your lease" },
                { icon: Clock, value: "15–20 hrs", label: "Of searching you skip" },
                { icon: CheckCircle, value: "Faster", label: "Approval with less stress" },
              ].map((item, i) => (
                <motion.div key={item.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="text-center bg-white/5 rounded-xl p-6 border border-white/10">
                  <item.icon className="w-8 h-8 text-[#DE6E4B] mx-auto mb-3" />
                  <p className="text-2xl font-bold text-white mb-1">{item.value}</p>
                  <p className="text-slate-400 text-sm">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 6: QUALIFICATION ───────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-main max-w-3xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-8 font-['Playfair_Display'] text-center" data-testid="qualification-title">
              Who This Is For
            </h2>
            <div className="space-y-4">
              {[
                { icon: Users, text: "Serious renters planning to move soon" },
                { icon: TrendingUp, text: "People who want the best deal, not just any apartment" },
                { icon: Shield, text: "Renters who want guidance and strategy" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-4 bg-[#0A2342]/[0.03] border border-[#0A2342]/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-[#DE6E4B]/10 rounded-lg flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-[#DE6E4B]" />
                  </div>
                  <p className="text-[#0A2342] text-sm md:text-base font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 7: FORM ───────── */}
      <section ref={formRef} className="py-16 md:py-20 bg-slate-50" id="apply-form" data-testid="concierge-form-section">
        <div className="container-main max-w-2xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-3 font-['Playfair_Display']">
                Apply for Concierge Service
              </h2>
              <p className="text-slate-500 text-sm md:text-base">
                Fill out the form below and we'll match you with the best deals for your situation.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-10 shadow-xl border border-slate-100 space-y-5" data-testid="concierge-form">
              {/* Name + Phone */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
                  <input type="text" required value={form.name} onChange={set("name")}
                    placeholder="Your full name" className="input-field" data-testid="form-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Phone Number *</label>
                  <input type="tel" required value={form.phone} onChange={set("phone")}
                    placeholder="(555) 123-4567" className="input-field" data-testid="form-phone" />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Email *</label>
                <input type="email" required value={form.email} onChange={set("email")}
                  placeholder="you@email.com" className="input-field" data-testid="form-email" />
              </div>

              {/* Budget + Move Date */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Budget Range *</label>
                  <div className="relative">
                    <select required value={form.budget} onChange={set("budget")}
                      className="input-field appearance-none pr-10" data-testid="form-budget">
                      <option value="">Select budget</option>
                      {budgetRanges.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Desired Move Date *</label>
                  <input type="date" required value={form.moveDate} onChange={set("moveDate")}
                    className="input-field" data-testid="form-move-date" />
                </div>
              </div>

              {/* Areas + Credit */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Preferred Areas <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input type="text" value={form.areas} onChange={set("areas")}
                    placeholder="e.g. Katy, Sugar Land" className="input-field" data-testid="form-areas" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Credit Range <span className="text-slate-400 font-normal">(optional)</span></label>
                  <div className="relative">
                    <select value={form.credit} onChange={set("credit")}
                      className="input-field appearance-none pr-10" data-testid="form-credit">
                      <option value="">Prefer not to say</option>
                      <option value="Excellent (750+)">Excellent (750+)</option>
                      <option value="Good (700-749)">Good (700-749)</option>
                      <option value="Fair (650-699)">Fair (650-699)</option>
                      <option value="Below 650">Below 650</option>
                      <option value="Not sure">Not sure</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Move Timeline */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Move Timeline</label>
                <div className="relative">
                  <select value={form.timeline} onChange={set("timeline")}
                    className="input-field appearance-none pr-10" data-testid="form-timeline">
                    <option value="">Select timeline</option>
                    <option value="0-30 days">0 – 30 days</option>
                    <option value="30-60 days">30 – 60 days</option>
                    <option value="60+ days">60+ days</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Biggest Challenge */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Biggest Challenge <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea value={form.challenge} onChange={set("challenge")}
                  rows={3} placeholder="What's been the hardest part of your apartment search?"
                  className="textarea-field" data-testid="form-challenge" />
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full py-4 bg-[#DE6E4B] hover:bg-[#c5593d] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#DE6E4B]/20 flex items-center justify-center gap-2 text-base disabled:opacity-60"
                data-testid="form-submit">
                {loading ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>Find My Best Options <ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              <p className="text-xs text-slate-400 text-center">
                Your info is private and only used to find you the best apartments.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* ───────── SECTION 8: CTA CLOSE ───────── */}
      <section className="py-16 md:py-20 bg-[#0A2342]">
        <div className="container-main max-w-2xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 font-['Playfair_Display']" data-testid="cta-close-title">
              Let's Find Your Best Apartment Deal
            </h2>
            <p className="text-slate-400 mb-8 max-w-md mx-auto text-sm md:text-base">
              Stop searching. Start saving. Our concierge team is ready to work for you.
            </p>
            <button onClick={scrollToForm}
              className="bg-[#DE6E4B] hover:bg-[#c5593d] text-white font-semibold px-10 py-4 rounded-lg transition-all shadow-lg shadow-[#DE6E4B]/25 inline-flex items-center gap-2 text-base"
              data-testid="cta-close-apply">
              Apply Now <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DealConciergePage;
